import moment from 'moment';
import {TimeRange} from '@flowmaps-com/flowmaps-typescriptmodels';

export function latestCompletedPortfolioWeiiYear(currentYear = moment().year()): number {
    return currentYear - 1;
}

export function selectAutomaticPortfolioWeiiYear(availableYears: number[], selectedRange?: TimeRange,
                                                  currentYear = moment().year()): number | null {
    const historicalYears = [...new Set(availableYears)]
        .filter(year => year < currentYear)
        .sort((a, b) => b - a);
    if (historicalYears.length === 0) {
        return [...new Set(availableYears)].sort((a, b) => b - a)[0] ?? null;
    }
    if (!selectedRange?.start || !selectedRange?.end) return historicalYears[0];

    const rangeStart = moment(selectedRange.start);
    const rangeEnd = moment(selectedRange.end);
    const fullyContained = historicalYears.find(year =>
        !moment({year, month: 0, day: 1}).isBefore(rangeStart)
        && !moment({year: year + 1, month: 0, day: 1}).isAfter(rangeEnd));
    if (fullyContained != null) return fullyContained;

    const selectedStartYear = rangeStart.year();
    if (historicalYears.includes(selectedStartYear)) return selectedStartYear;

    const intersected = historicalYears.find(year => {
        const yearStart = moment({year, month: 0, day: 1});
        const yearEnd = moment({year: year + 1, month: 0, day: 1});
        return yearStart.isBefore(rangeEnd) && yearEnd.isAfter(rangeStart);
    });
    return intersected ?? historicalYears[0];
}

export function portfolioWeiiYearRange(year: number): TimeRange {
    return {
        start: moment({year, month: 0, day: 1}).format('YYYY-MM-DDTHH:mm:ss'),
        end: moment({year: year + 1, month: 0, day: 1}).format('YYYY-MM-DDTHH:mm:ss')
    };
}

export function portfolioWeiiAvailableYears(selectedRange: TimeRange,
                                             currentYear = moment().year()): number[] {
    if (!selectedRange?.end) return [latestCompletedPortfolioWeiiYear(currentYear)];
    const latestIncludedYear = moment(selectedRange.end).subtract(1, 'millisecond').year();
    const firstYear = latestIncludedYear - 3;
    const lastYear = Math.max(latestIncludedYear, currentYear);
    return Array.from({length: lastYear - firstYear + 1}, (_, index) => lastYear - index);
}

export function previousAvailablePortfolioWeiiYear(availableYears: number[], primaryYear: number): number | null {
    return [...new Set(availableYears)].filter(year => year < primaryYear).sort((a, b) => b - a)[0] ?? null;
}

export function isPortfolioWeiiYearProvisional(year: number, currentYear = moment().year()): boolean {
    return year >= currentYear;
}

export function normalizePortfolioWeiiYearSetting(value: unknown): number | null {
    if (value == null || value === '' || value === 'automatic') return null;
    const year = Number(value);
    return Number.isInteger(year) && year >= 1900 && year <= 9999 ? year : null;
}

export interface PortfolioWeiiYearSelection {
    primaryYear: number | null;
    comparisonYear: number | null;
}

export function resolvePortfolioWeiiYearSelection(availableYears: number[], automaticYear: number | null,
                                                  calculationYear: number | null, compareYears: boolean,
                                                  requestedComparisonYear: number | null): PortfolioWeiiYearSelection {
    const primaryYear = calculationYear != null && availableYears.includes(calculationYear)
        ? calculationYear : automaticYear;
    if (!compareYears || primaryYear == null) return {primaryYear, comparisonYear: null};

    const comparisonYear = requestedComparisonYear != null
        && requestedComparisonYear !== primaryYear
        && availableYears.includes(requestedComparisonYear)
        ? requestedComparisonYear : previousAvailablePortfolioWeiiYear(availableYears, primaryYear);
    return {primaryYear, comparisonYear};
}
